import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { useBrands } from "../contexts/BrandContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "../components/ui/dialog";
import { Plus, Pencil, Trash2, Contact, Phone, BarChart3, FolderOpen, AlertTriangle, CheckCircle2, Loader2, CalendarClock } from "lucide-react";
import { toast } from "sonner";
import SearchableSelect from "../components/SearchableSelect";
import BeneficiaryStatsDialog from "../components/BeneficiaryStatsDialog";
import HorizontalScrollContainer from "../components/HorizontalScrollContainer";
import StickyPageHeader from "../components/StickyPageHeader";

const EMPTY = {
  denomination: "",
  category_id: "",
  brand_ids: [],
  phone_country_code: "+33",
  phone_number: "",
  email: "",
  address: "",
  country: "France",
  notes: "",
};

const COUNTRY_CODES = ["+33", "+225", "+1", "+44", "+32", "+41", "+49", "+34", "+212", "+213", "+216", "+221", "+237"];

export default function ContactsBeneficiaires() {
  const { user } = useAuth();
  const { brands, selectedBrandId } = useBrands();
  const [list, setList] = useState([]);
  const [cats, setCats] = useState([]);
  const [search, setSearch] = useState("");
  const [filterCat, setFilterCat] = useState("");
  const [filterBrand, setFilterBrand] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  // Stats panel
  const [statsOpen, setStatsOpen] = useState(false);
  const [statsTarget, setStatsTarget] = useState(null);

  // Dé-doublonnage temps réel
  const [dupState, setDupState] = useState({ loading: false, matches: [] });
  const dupTimerRef = useRef(null);

  const canManage = user?.role === "admin" || user?.role === "manager" || user?.role === "gestionnaire";

  const load = async () => {
    try {
      const params = selectedBrandId ? { brand_id: selectedBrandId } : {};
      const [bs, cs] = await Promise.all([
        api.get("/beneficiaries", { params }),
        api.get("/beneficiary-categories", { params }),
      ]);
      setList(bs.data || []);
      setCats(cs.data || []);
    } catch {
      setList([]); setCats([]);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [selectedBrandId]);

  const openCreate = () => {
    setEditing(null);
    setForm({
      ...EMPTY,
      category_id: cats[0]?.id || "",
      brand_ids: brands[0]?.id ? [brands[0].id] : [],
    });
    setOpen(true);
  };

  const openEdit = (b) => {
    setEditing(b);
    setForm({
      denomination: b.denomination || "",
      category_id: b.category_id || "",
      brand_ids: b.brand_ids || [],
      phone_country_code: b.phone_country_code || "+33",
      phone_number: b.phone_number || "",
      email: b.email || "",
      address: b.address || "",
      country: b.country || "",
      notes: b.notes || "",
    });
    setOpen(true);
  };

  const toggleBrand = (bid) => {
    setForm((f) => {
      const set = new Set(f.brand_ids || []);
      if (set.has(bid)) set.delete(bid); else set.add(bid);
      return { ...f, brand_ids: Array.from(set) };
    });
  };

  const save = async () => {
    if (!form.denomination.trim()) { toast.error("Dénomination requise"); return; }
    if (!form.category_id) { toast.error("Catégorie requise"); return; }
    if (!form.brand_ids || form.brand_ids.length === 0) { toast.error("Au moins un département"); return; }
    if (dupState.matches.length > 0) {
      toast.error(`Contact déjà existant : ${dupState.matches[0].denomination}`);
      return;
    }
    try {
      if (editing) {
        await api.put(`/beneficiaries/${editing.id}`, form);
        toast.success("Bénéficiaire mis à jour");
      } else {
        await api.post("/beneficiaries", form);
        toast.success("Bénéficiaire créé");
      }
      setOpen(false);
      load();
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.code === "duplicate_beneficiary") {
        toast.error(detail.message || "Contact déjà existant");
        setDupState({ loading: false, matches: detail.matches || [] });
      } else {
        toast.error(typeof detail === "string" ? detail : "Erreur");
      }
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer ce bénéficiaire ?")) return;
    try {
      await api.delete(`/beneficiaries/${id}`);
      toast.success("Supprimé");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  // ---- Stats ----
  const openStats = (b) => {
    setStatsTarget(b);
    setStatsOpen(true);
  };

  // ---- Dé-doublonnage temps réel (debounce 350ms) ----
  useEffect(() => {
    if (!open) return; // n'exécute que quand la modale est ouverte
    if (dupTimerRef.current) clearTimeout(dupTimerRef.current);
    const denom = (form.denomination || "").trim();
    const email = (form.email || "").trim();
    const num = (form.phone_number || "").trim();
    // Rien à vérifier tant qu'il n'y a pas au moins un signal pertinent
    if (!(denom && form.category_id) && !email && !num) {
      setDupState({ loading: false, matches: [] });
      return;
    }
    setDupState((s) => ({ ...s, loading: true }));
    dupTimerRef.current = setTimeout(async () => {
      try {
        const { data } = await api.post("/beneficiaries/check-duplicate", {
          denomination: denom,
          category_id: form.category_id,
          email,
          phone_country_code: form.phone_country_code,
          phone_number: num,
          exclude_id: editing?.id,
        });
        setDupState({ loading: false, matches: data.matches || [] });
      } catch {
        setDupState({ loading: false, matches: [] });
      }
    }, 350);
    return () => {
      if (dupTimerRef.current) clearTimeout(dupTimerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, form.denomination, form.category_id, form.email, form.phone_country_code, form.phone_number, editing?.id]);

  // Reset état dup à la fermeture
  useEffect(() => {
    if (!open) setDupState({ loading: false, matches: [] });
  }, [open]);

  const dateLabel = (iso) => {
    if (!iso) return "—";
    try {
      const d = new Date(iso);
      return d.toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" });
    } catch { return "—"; }
  };
  const dateTitle = (iso) => {
    if (!iso) return "";
    try {
      return new Date(iso).toLocaleString("fr-FR", { day: "2-digit", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit" });
    } catch { return ""; }
  };

  const filtered = list
    .filter((b) => {
      if (search && !b.denomination.toLowerCase().includes(search.toLowerCase())) return false;
      if (filterCat && b.category_id !== filterCat) return false;
      if (filterBrand && !(b.brand_ids || []).includes(filterBrand)) return false;
      return true;
    })
    .sort((a, b) =>
      (a.denomination || "").localeCompare(b.denomination || "", "fr", { sensitivity: "base" })
    );

  const dupReasonLabel = (r) =>
    r === "email" ? "Email identique"
    : r === "phone" ? "Téléphone identique"
    : r === "denomination" ? "Même dénomination & catégorie"
    : r;

  return (
    <div className="space-y-6" data-testid="contacts-beneficiaires-page">
      <StickyPageHeader testid="contacts-sticky-header">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <p className="label-uppercase mb-2">Base de données interne</p>
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-display font-bold tracking-tight text-zinc-900">
            Contacts & bénéficiaires
          </h1>
          <p className="text-sm text-zinc-600 mt-1 font-mono">
            Clientèle, partenaires, fournisseurs — utilisés pour planifier les interventions. Pas d'accès plateforme.
          </p>
        </div>
        {canManage && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button onClick={openCreate} className="bg-[#D97706] hover:bg-[#B45309] rounded-sm text-white" data-testid="new-beneficiary-btn">
                <Plus size={14} className="mr-1" /> Nouveau bénéficiaire
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-xl">
              <DialogHeader>
                <DialogTitle>{editing ? "Modifier le bénéficiaire" : "Nouveau bénéficiaire"}</DialogTitle>
                <DialogDescription>
                  Coordonnées et rattachement département. Aucune donnée ne donne accès à la plateforme.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label className="label-uppercase mb-1.5 block">Dénomination</Label>
                  <Input value={form.denomination} onChange={(e) => setForm({ ...form, denomination: e.target.value })} placeholder="Nom ou raison sociale" data-testid="ben-denom" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="label-uppercase mb-1.5 block">Catégorie</Label>
                    <SearchableSelect
                      value={form.category_id}
                      onChange={(v) => setForm({ ...form, category_id: v })}
                      options={cats.map((c) => ({ value: c.id, label: c.name }))}
                      placeholder="—"
                      searchPlaceholder="Rechercher une catégorie…"
                      data-testid="ben-cat"
                    />
                  </div>
                  <div>
                    <Label className="label-uppercase mb-1.5 block">Pays</Label>
                    <Input value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} placeholder="France" data-testid="ben-country" />
                  </div>
                </div>
                <div>
                  <Label className="label-uppercase mb-1.5 block">Départements rattachés</Label>
                  <div className="flex flex-wrap gap-2" data-testid="ben-brands-multi">
                    {brands.map((b) => {
                      const active = (form.brand_ids || []).includes(b.id);
                      return (
                        <button key={b.id} type="button" onClick={() => toggleBrand(b.id)}
                          className={`text-xs px-2.5 py-1 rounded-sm border transition-colors ${active ? "border-zinc-900 bg-zinc-900 text-white" : "border-zinc-300 bg-white text-zinc-700 hover:border-zinc-400"}`}
                          data-testid={`ben-brand-opt-${b.id}`}>
                          <span className="inline-block h-2 w-2 rounded-full mr-1.5 align-middle" style={{ backgroundColor: b.color }} />
                          {b.name}
                        </button>
                      );
                    })}
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <Label className="label-uppercase mb-1.5 block">Indicatif</Label>
                    <Select value={form.phone_country_code} onValueChange={(v) => setForm({ ...form, phone_country_code: v })}>
                      <SelectTrigger data-testid="ben-phone-cc"><SelectValue /></SelectTrigger>
                      <SelectContent>{COUNTRY_CODES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="col-span-2">
                    <Label className="label-uppercase mb-1.5 block">Téléphone</Label>
                    <Input value={form.phone_number} onChange={(e) => setForm({ ...form, phone_number: e.target.value })} placeholder="6 12 34 56 78" data-testid="ben-phone" />
                  </div>
                </div>
                <div>
                  <Label className="label-uppercase mb-1.5 block">Email (optionnel)</Label>
                  <Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} type="email" placeholder="contact@…" data-testid="ben-email" />
                </div>
                <div>
                  <Label className="label-uppercase mb-1.5 block">Adresse</Label>
                  <Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} placeholder="Adresse complète" data-testid="ben-address" />
                </div>
                <div>
                  <Label className="label-uppercase mb-1.5 block">Notes</Label>
                  <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} data-testid="ben-notes" />
                </div>
                {/* Dé-doublonnage temps réel */}
                {dupState.loading && (
                  <div className="flex items-center gap-2 text-xs font-mono text-zinc-500" data-testid="ben-dup-loading">
                    <Loader2 size={12} className="animate-spin" /> Recherche de contacts similaires…
                  </div>
                )}
                {!dupState.loading && dupState.matches.length > 0 && (
                  <div className="bg-red-50 border border-red-200 rounded-sm p-3" data-testid="ben-dup-warning">
                    <div className="flex items-start gap-2">
                      <AlertTriangle size={16} className="text-red-600 flex-shrink-0 mt-0.5" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-red-800">
                          Ce contact existe déjà dans la base
                        </p>
                        <p className="text-[11px] font-mono text-red-700 mt-1">
                          La création est bloquée. Modifiez les informations ou ouvrez la fiche existante.
                        </p>
                        <ul className="mt-2 space-y-1.5">
                          {dupState.matches.slice(0, 3).map((m) => (
                            <li
                              key={m.id}
                              className="flex items-center justify-between gap-2 bg-white border border-red-100 rounded-sm px-2 py-1.5"
                              data-testid={`ben-dup-match-${m.id}`}
                            >
                              <div className="flex-1 min-w-0">
                                <p className="text-xs font-semibold text-zinc-900 truncate">{m.denomination}</p>
                                <p className="text-[10px] font-mono text-zinc-500 truncate">
                                  {(m.match_reasons || []).map(dupReasonLabel).join(" · ")}
                                </p>
                              </div>
                              <Link
                                to={`/beneficiaires/${m.id}/dossier`}
                                className="text-[10px] font-mono text-[#0055FF] hover:underline whitespace-nowrap"
                                onClick={() => setOpen(false)}
                                data-testid={`ben-dup-open-${m.id}`}
                              >
                                Ouvrir →
                              </Link>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                )}
                {!dupState.loading && dupState.matches.length === 0 &&
                  (form.denomination || form.email || form.phone_number) && (
                  <div className="flex items-center gap-2 text-[11px] font-mono text-emerald-700" data-testid="ben-dup-clean">
                    <CheckCircle2 size={12} /> Aucun doublon détecté
                  </div>
                )}
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)}>Annuler</Button>
                <Button
                  onClick={save}
                  disabled={dupState.matches.length > 0 || dupState.loading}
                  className="bg-[#D97706] hover:bg-[#B45309] text-white disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="ben-save-btn"
                >
                  Enregistrer
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <div className="bg-white border border-zinc-200 rounded-sm p-3 flex flex-wrap gap-3 items-end">
        <div className="flex-1 min-w-[200px]">
          <Label className="label-uppercase mb-1.5 block">Recherche</Label>
          <Input placeholder="Dénomination…" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="ben-search" />
        </div>
        <div className="min-w-[180px]">
          <Label className="label-uppercase mb-1.5 block">Catégorie</Label>
          <SearchableSelect
            value={filterCat || "all"}
            onChange={(v) => setFilterCat(v === "all" ? "" : v)}
            options={[{ value: "all", label: "Toutes" }, ...cats.map((c) => ({ value: c.id, label: c.name }))]}
            placeholder="Toutes"
            searchPlaceholder="Rechercher…"
            data-testid="ben-filter-cat"
          />
        </div>
        <div className="min-w-[180px]">
          <Label className="label-uppercase mb-1.5 block">Département</Label>
          <SearchableSelect
            value={filterBrand || "all"}
            onChange={(v) => setFilterBrand(v === "all" ? "" : v)}
            options={[{ value: "all", label: "Tous" }, ...brands.map((b) => ({ value: b.id, label: b.name }))]}
            placeholder="Tous"
            searchPlaceholder="Rechercher…"
            data-testid="ben-filter-brand"
          />
        </div>
      </div>
      </StickyPageHeader>

      <HorizontalScrollContainer testId="contacts-table-scroll" className="bg-white border border-zinc-200 rounded-sm hidden md:block">
        <table className="w-full text-sm min-w-[1140px]">
          <thead className="bg-zinc-50 text-zinc-600 text-[10px] uppercase tracking-wider">
            <tr>
              <th className="text-left px-4 py-3">Dénomination</th>
              <th className="text-left px-4 py-3">Catégorie</th>
              <th className="text-left px-4 py-3">Départements</th>
              <th className="text-left px-4 py-3">Email</th>
              <th className="text-left px-4 py-3">Téléphone</th>
              <th className="text-left px-4 py-3">Pays</th>
              <th className="text-left px-4 py-3 whitespace-nowrap">Créé par</th>
              <th className="text-left px-4 py-3 whitespace-nowrap">Créé le</th>
              <th className="text-right px-4 py-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((b) => {
              const cat = cats.find((c) => c.id === b.category_id);
              const bs = brands.filter((x) => (b.brand_ids || []).includes(x.id));
              return (
                <tr key={b.id} className="border-t border-zinc-100 hover:bg-zinc-50/50" data-testid={`beneficiary-row-${b.id}`}>
                  <td className="px-4 py-3 font-medium text-zinc-900">
                    {b.denomination}
                  </td>
                  <td className="px-4 py-3">
                    {cat && (
                      <span className="inline-flex items-center gap-1.5 text-[11px] font-mono px-2 py-0.5 rounded-sm" style={{ backgroundColor: `${cat.color}20`, color: cat.color }}>
                        {cat.name}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-1">
                      {bs.map((x) => (
                        <span key={x.id} className="text-[11px] font-mono inline-flex items-center gap-1">
                          <span className="h-2 w-2 rounded-full" style={{ backgroundColor: x.color }} />
                          {x.name}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-zinc-700 font-mono text-xs">
                    {b.email ? (
                      <a href={`mailto:${b.email}`} className="hover:text-[#0055FF] hover:underline break-all" data-testid={`beneficiary-email-${b.id}`}>
                        {b.email}
                      </a>
                    ) : "—"}
                  </td>
                  <td className="px-4 py-3 text-zinc-700 font-mono text-xs">
                    {b.phone_number ? (
                      <span className="inline-flex items-center gap-1"><Phone size={11} /> {b.phone_country_code} {b.phone_number}</span>
                    ) : "—"}
                  </td>
                  <td className="px-4 py-3 text-zinc-700 text-xs">{b.country || "—"}</td>
                  <td
                    className="px-4 py-3 text-zinc-700 font-mono text-xs whitespace-nowrap"
                    data-testid={`beneficiary-created-by-${b.id}`}
                  >
                    {b.created_by_name || <span className="text-zinc-400">—</span>}
                  </td>
                  <td
                    className="px-4 py-3 text-zinc-600 font-mono text-xs whitespace-nowrap"
                    title={dateTitle(b.created_at)}
                    data-testid={`beneficiary-created-${b.id}`}
                  >
                    {dateLabel(b.created_at)}
                  </td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    {canManage && (
                      <Link
                        to={`/beneficiaires/${b.id}/dossier`}
                        className="inline-flex items-center justify-center h-8 w-8 rounded-sm hover:bg-zinc-100 text-[#D97706]"
                        title="Ouvrir le dossier"
                        data-testid={`open-dossier-ben-${b.id}`}
                      >
                        <FolderOpen size={14} />
                      </Link>
                    )}
                    {canManage && (
                      <Button variant="ghost" size="sm" onClick={() => openStats(b)} title="Statistiques d'interventions" data-testid={`stats-beneficiary-${b.id}`}><BarChart3 size={12} className="text-[#0055FF]" /></Button>
                    )}
                    {canManage && (
                      <Button variant="ghost" size="sm" onClick={() => openEdit(b)} data-testid={`edit-beneficiary-${b.id}`}><Pencil size={12} /></Button>
                    )}
                    {canManage && (
                      <Button variant="ghost" size="sm" className="text-red-600" onClick={() => remove(b.id)} data-testid={`delete-beneficiary-${b.id}`}><Trash2 size={12} /></Button>
                    )}
                  </td>
                </tr>
              );
            })}
            {filtered.length === 0 && (
              <tr>
                <td colSpan={9} className="px-4 py-12 text-center text-zinc-500 font-mono">
                  Aucun bénéficiaire
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </HorizontalScrollContainer>

      {/* Vue mobile : cartes empilées */}
      <div className="md:hidden space-y-3" data-testid="contacts-mobile-list">
        {filtered.map((b) => {
          const cat = cats.find((c) => c.id === b.category_id);
          const bs = brands.filter((x) => (b.brand_ids || []).includes(x.id));
          return (
            <div
              key={b.id}
              className="bg-white border border-zinc-200 rounded-sm p-3"
              data-testid={`beneficiary-card-${b.id}`}
            >
              <div className="flex items-start justify-between gap-2 mb-2">
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-zinc-900 truncate">{b.denomination}</p>
                  {cat && (
                    <span className="inline-flex items-center gap-1 text-[10px] font-mono px-1.5 py-0.5 rounded-sm mt-1"
                          style={{ backgroundColor: `${cat.color}20`, color: cat.color }}>
                      {cat.name}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-0.5 flex-shrink-0">
                  {canManage && (
                    <Link
                      to={`/beneficiaires/${b.id}/dossier`}
                      className="inline-flex items-center justify-center h-8 w-8 rounded-sm hover:bg-zinc-100 text-[#D97706]"
                      title="Dossier"
                      data-testid={`open-dossier-ben-mobile-${b.id}`}
                    >
                      <FolderOpen size={14} />
                    </Link>
                  )}
                  {canManage && (
                    <Button variant="ghost" size="sm" onClick={() => openStats(b)} className="h-8 w-8 p-0" title="Stats">
                      <BarChart3 size={12} className="text-[#0055FF]" />
                    </Button>
                  )}
                  {canManage && (
                    <Button variant="ghost" size="sm" onClick={() => openEdit(b)} className="h-8 w-8 p-0" title="Modifier">
                      <Pencil size={12} />
                    </Button>
                  )}
                  {canManage && (
                    <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-red-600" onClick={() => remove(b.id)} title="Supprimer">
                      <Trash2 size={12} />
                    </Button>
                  )}
                </div>
              </div>
              {bs.length > 0 && (
                <div className="flex flex-wrap gap-1 mb-2">
                  {bs.map((x) => (
                    <span key={x.id} className="text-[10px] font-mono inline-flex items-center gap-1">
                      <span className="h-2 w-2 rounded-full" style={{ backgroundColor: x.color }} />
                      {x.name}
                    </span>
                  ))}
                </div>
              )}
              <dl className="grid grid-cols-1 gap-1 text-xs font-mono text-zinc-600">
                {b.email && (
                  <div className="flex items-start gap-1.5">
                    <span className="text-zinc-400 shrink-0 w-14">Email</span>
                    <a href={`mailto:${b.email}`} className="hover:text-[#0055FF] break-all">{b.email}</a>
                  </div>
                )}
                {b.phone_number && (
                  <div className="flex items-center gap-1.5">
                    <span className="text-zinc-400 shrink-0 w-14">Tél.</span>
                    <span className="inline-flex items-center gap-1"><Phone size={10} /> {b.phone_country_code} {b.phone_number}</span>
                  </div>
                )}
                <div className="flex items-center gap-1.5" data-testid={`beneficiary-created-mobile-${b.id}`}>
                  <span className="text-zinc-400 shrink-0 w-14">Créé</span>
                  <span className="inline-flex items-center gap-1" title={dateTitle(b.created_at)}>
                    <CalendarClock size={10} /> {dateLabel(b.created_at)}
                  </span>
                </div>
                {b.created_by_name && (
                  <div className="flex items-center gap-1.5" data-testid={`beneficiary-created-by-mobile-${b.id}`}>
                    <span className="text-zinc-400 shrink-0 w-14">Par</span>
                    <span className="truncate">{b.created_by_name}</span>
                  </div>
                )}
              </dl>
            </div>
          );
        })}
        {filtered.length === 0 && (
          <div className="bg-white border border-zinc-200 rounded-sm py-12 text-center text-zinc-500 font-mono">
            Aucun bénéficiaire
          </div>
        )}
      </div>

      {/* Stats dialog */}
      <BeneficiaryStatsDialog
        open={statsOpen}
        onOpenChange={setStatsOpen}
        beneficiary={statsTarget}
        brands={brands}
      />
    </div>
  );
}
